import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Avatar,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardMedia,
  TextField,
} from '@mui/material';
import {
  Delete,
  Visibility,
  Close,
  Star,
  Person,
  Store,
  ShoppingBag,
  Edit as EditIcon,
} from '@mui/icons-material';
import reviewService from '../services/reviewService';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState(null);

  // Edit State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [reviewToEdit, setReviewToEdit] = useState(null);
  const [editRating, setEditRating] = useState(0);
  const [editComment, setEditComment] = useState('');

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await reviewService.getAllReviews();
      if (response.data.success) {
        setReviews(response.data.reviews || []);
      }
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError(err.response?.data?.message || 'Failed to fetch reviews');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (review) => {
    setReviewToDelete(review);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!reviewToDelete) return;

    try {
      await reviewService.deleteReview(reviewToDelete._id);
      setReviews(reviews.filter(r => r._id !== reviewToDelete._id));
      setDeleteDialogOpen(false);
      setReviewToDelete(null);
    } catch (err) {
      console.error('Error deleting review:', err);
      setError(err.response?.data?.message || 'Failed to delete review');
    }
  };

  const handleEditClick = (review) => {
    setReviewToEdit(review);
    setEditRating(review.rating);
    setEditComment(review.comment);
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      const response = await reviewService.updateReview(reviewToEdit._id, {
        rating: editRating,
        comment: editComment
      });

      if (response.data.success) {
        // Update local state
        setReviews(reviews.map(r =>
          r._id === reviewToEdit._id ? response.data.review : r
        ));
        setEditDialogOpen(false);
        setReviewToEdit(null);
      }
    } catch (err) {
      console.error('Error updating review:', err);
      setError(err.response?.data?.message || 'Failed to update review');
    }
  };

  const handleViewImages = (images) => {
    if (images && images.length > 0) {
      setSelectedImages(images);
      setImageDialogOpen(true);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return '#4caf50';
    if (rating >= 3) return '#ff9800';
    return '#f44336';
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          color: '#000000',
          fontWeight: 700,
          pb: 2,
          mb: 3,
          borderBottom: '2px solid #ff2852',
        }}
      >
        Reviews Management
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Paper sx={{
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}>
        {!reviews || reviews.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Reviews Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Reviews will appear here once customers start reviewing products.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{
                  backgroundColor: '#fafafa',
                }}>
                  <TableCell sx={{ fontWeight: 700, color: '#000000', borderBottom: '2px solid #ff2852' }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#000000', borderBottom: '2px solid #ff2852' }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#000000', borderBottom: '2px solid #ff2852' }}>Seller</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#000000', borderBottom: '2px solid #ff2852' }}>Rating</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#000000', borderBottom: '2px solid #ff2852' }}>Comment</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#000000', borderBottom: '2px solid #ff2852' }}>Images</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#000000', borderBottom: '2px solid #ff2852' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#000000', borderBottom: '2px solid #ff2852' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reviews.map((review) => (
                  <TableRow
                    key={review._id}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#fffef5',
                      },
                    }}
                  >
                    {/* User */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={review.user?.profileImage}
                          sx={{ width: 32, height: 32, bgcolor: '#ff2852' }}
                        >
                          {review.user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {review.user?.name || 'Anonymous'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {review.user?.email || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Product */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {review.product?.images?.[0] && (
                          <Box
                            component="img"
                            src={review.product.images[0]}
                            alt={review.product.name}
                            sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }}
                          />
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {review.product?.name || 'Product Deleted'}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Seller */}
                    <TableCell>
                      {review.seller ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Store sx={{ fontSize: 16, color: '#ff2852' }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {review.seller.brandName || review.seller.name || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Chip label="Khizly Store" size="small" color="primary" icon={<Store />} />
                      )}
                    </TableCell>

                    {/* Rating */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Rating
                          value={review.rating}
                          readOnly
                          size="small"
                          sx={{
                            '& .MuiRating-iconFilled': {
                              color: getRatingColor(review.rating),
                            },
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: getRatingColor(review.rating) }}>
                          {review.rating}/5
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Comment */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {review.comment || 'No comment'}
                      </Typography>
                    </TableCell>

                    {/* Images */}
                    <TableCell>
                      {review.images && review.images.length > 0 ? (
                        <IconButton
                          size="small"
                          onClick={() => handleViewImages(review.images)}
                          sx={{ color: '#ff2852', '&:hover': { backgroundColor: 'rgba(189, 0, 0, 0.1)' } }}
                        >
                          <Visibility />
                          <Typography variant="caption" sx={{ ml: 0.5 }}>({review.images.length})</Typography>
                        </IconButton>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No images</Typography>
                      )}
                    </TableCell>

                    {/* Date */}
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(review.createdAt)}
                      </Typography>
                    </TableCell>

                    {/* Actions */}
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(review)}
                        sx={{ color: '#1976d2', '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' } }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(review)}
                        sx={{ color: '#f44336', '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.1)' } }}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Image Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Review Images</Typography>
          <IconButton onClick={() => setImageDialogOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {selectedImages.map((imageUrl, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card>
                  <CardMedia
                    component="img"
                    image={imageUrl}
                    alt={`Review image ${index + 1}`}
                    sx={{ height: 300, objectFit: 'contain' }}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Review</DialogTitle>
        <DialogContent sx={{ minWidth: 400, pt: 2 }}>
          {reviewToEdit && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box>
                <Typography component="legend">Rating</Typography>
                <Rating
                  name="simple-controlled"
                  value={editRating}
                  onChange={(event, newValue) => {
                    setEditRating(newValue);
                  }}
                />
              </Box>
              <TextField
                label="Comment"
                multiline
                rows={4}
                value={editComment}
                onChange={(e) => setEditComment(e.target.value)}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Review</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this review? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Reviews;
